import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, Trash2, X } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';

/*
  Bell-icon dropdown for the in-app notification center
  (audit Phase 2 §6.2).

  Polls /notifications/summary every 30s for an unread count and
  loads the first page of /notifications when the dropdown opens.
  The icon tints by highest unread severity:

    info     → muted
    success  → primary (green)
    warning  → amber
    error    → destructive (red)
*/

const SEVERITY_COLOR = {
  info: 'text-muted-foreground',
  success: 'text-primary',
  warning: 'text-amber-500',
  error: 'text-destructive',
};

const POLL_MS = 30_000;

export default function NotificationBell() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({ unread_count: 0, highest_severity: null });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const fetchSummary = async () => {
    try {
      const r = await axios.get(`${API}/notifications/summary`);
      setSummary(r.data || { unread_count: 0, highest_severity: null });
    } catch (e) {
      // Silent — don't spam the operator if a 401 happens during refresh.
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications`, {
        params: { page: 1, page_size: 20 },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (open) fetchItems();
  }, [open]);

  // Close on outside-click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      fetchSummary();
    } catch (e) {
      // ignore
    }
  };

  const deleteOne = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      fetchSummary();
    } catch (e) {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      fetchSummary();
    } catch (e) {
      // ignore
    }
  };

  const tint = SEVERITY_COLOR[summary.highest_severity] || 'text-muted-foreground';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="xp-topbar-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('Notifications')}
        data-testid="topbar-bell"
        style={{
          background: 'transparent',
          border: 'none',
          position: 'relative',
          cursor: 'pointer',
          padding: 6,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 0,
        }}
      >
        <Bell className={`w-4 h-4 ${tint}`} />
        {summary.unread_count > 0 && (
          <span
            data-testid="topbar-bell-count"
            style={{
              position: 'absolute',
              top: 1,
              right: 0,
              minWidth: 15,
              height: 15,
              padding: '0 4px',
              boxSizing: 'border-box',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-sans)',
              letterSpacing: 0,
              boxShadow: '0 0 0 2px rgb(var(--bg))',
              borderRadius: 999,
              background:
                summary.highest_severity === 'error'
                  ? 'rgb(var(--destructive))'
                  : summary.highest_severity === 'warning'
                  ? '#f59e0b'
                  : 'rgb(var(--primary))',
              color: 'white',
              fontSize: 9,
              lineHeight: '14px',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {summary.unread_count > 99 ? '99+' : summary.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="topbar-bell-panel"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            width: 360,
            maxHeight: 480,
            overflowY: 'auto',
            background: 'rgb(var(--popover))',
            color: 'rgb(var(--popover-foreground))',
            border: '1px solid rgb(var(--border))',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            zIndex: 50,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              borderBottom: '1px solid rgb(var(--border))',
            }}
          >
            <strong>{t('Notifications')}</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              {summary.unread_count > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-xs text-primary hover:underline"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <Check className="w-3 h-3 inline mr-1" />
                  {t('Mark all read')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                aria-label={t('Close')}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 24, textAlign: 'center' }} className="text-muted-foreground">
              {t('Loading…')}
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }} className="text-muted-foreground">
              {t('No notifications yet')}
            </div>
          ) : (
            items.map((n) => {
              const inner = (
                <div style={{ flex: 1 }}>
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 3,
                        background:
                          n.severity === 'error'
                            ? 'rgb(var(--destructive))'
                            : n.severity === 'warning'
                            ? '#f59e0b'
                            : n.severity === 'success'
                            ? 'rgb(var(--primary))'
                            : 'rgb(var(--muted-foreground))',
                      }}
                    />
                    <span className={`text-sm font-medium ${n.is_read ? 'text-muted-foreground' : ''}`}>
                      {n.title}
                    </span>
                  </div>
                  {n.body && (
                    <div className="text-xs text-muted-foreground mt-1">{n.body}</div>
                  )}
                </div>
              );
              return (
                <div
                  key={n.id}
                  style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid rgb(var(--border))',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                  }}
                >
                  {n.link ? (
                    <Link to={n.link} onClick={() => { markRead(n.id); setOpen(false); }} style={{ flex: 1, color: 'inherit', textDecoration: 'none' }}>
                      {inner}
                    </Link>
                  ) : (
                    inner
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {!n.is_read && (
                      <button
                        type="button"
                        onClick={() => markRead(n.id)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                        aria-label={t('Mark read')}
                        title={t('Mark read')}
                      >
                        <Check className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => deleteOne(n.id)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                      aria-label={t('Delete')}
                      title={t('Delete')}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
